import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home as HomeIcon, Sun, Heart, CheckCircle, Clock, Shield, Users, DollarSign, X, Plus, Minus, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { services, pricingPlaceholder, additionalServices, detailedPricing } from '../mock';

const Services = () => {
  const [spaBathsOpen, setSpaBathsOpen] = useState(false);
  const [bathQuantity, setBathQuantity] = useState(1);
  const navigate = useNavigate();

  const handleAddToBooking = () => {
    // Store in sessionStorage to pass to booking page
    sessionStorage.setItem('spaBaths', JSON.stringify({ quantity: bathQuantity, price: 45 * bathQuantity }));
    setSpaBathsOpen(false);
    navigate('/booking');
  };

  const getIcon = (iconName) => {
    const icons = {
      home: HomeIcon,
      sun: Sun,
      heart: Heart
    };
    const Icon = icons[iconName] || HomeIcon;
    return <Icon className="h-12 w-12" />;
  };

  const whyChooseUs = [
    {
      icon: Shield,
      title: "Safe & Secure",
      description: "24/7 supervision and secure facilities for your peace of mind"
    },
    {
      icon: Users,
      title: "Experienced Staff",
      description: "Trained professionals who genuinely love and care for dogs"
    },
    {
      icon: Clock,
      title: "Flexible Hours",
      description: "Convenient drop-off and pick-up times to fit your schedule"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Spa Baths Popup Modal */}
      {spaBathsOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setSpaBathsOpen(false)}>
          <div 
            className="bg-background rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-primary p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-8 w-8" />
                  <h2 className="text-2xl font-bold">Spa Baths</h2>
                </div>
                <button onClick={() => setSpaBathsOpen(false)} className="hover:bg-white/20 rounded-full p-1">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <p className="text-3xl font-bold mt-4">$45 <span className="text-lg font-normal">/ dog per bath</span></p>
            </div>
            
            <div className="p-6 space-y-4">
              <h3 className="font-semibold text-foreground">What's Included:</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Spa wash and massage with lavender infused hypoallergenic shampoo</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Luxurious Conditional Soak and rinse</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Short or Long hair De-shedding</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>Full Puppy Blowout, followed by paw pad moisturizing conditioner</span>
                </li>
              </ul>
              
              <div className="border-t pt-4 mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">Number of Baths:</label>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setBathQuantity(Math.max(1, bathQuantity - 1))}
                    className="w-10 h-10 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span className="text-2xl font-bold w-12 text-center">{bathQuantity}</span>
                  <button 
                    onClick={() => setBathQuantity(bathQuantity + 1)}
                    className="w-10 h-10 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Total: <span className="font-bold text-foreground">${45 * bathQuantity}</span>
                </p>
              </div>
              
              <Button onClick={handleAddToBooking} className="w-full mt-4" size="lg">
                Add to Booking
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-secondary/30 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Professional Dog Care Services
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Comprehensive boarding solutions tailored to your dog's needs in Mission, BC
            </p>
            <Link to="/booking">
              <Button size="lg">Book Your Dog's Stay</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {services.map((service, index) => (
              <Card key={service.id} className="overflow-hidden border-2 hover:border-primary/50 transition-all">
                <div className={`grid md:grid-cols-2 gap-8 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className="p-8">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-6">
                      {getIcon(service.icon)}
                    </div>
                    <CardTitle className="text-3xl mb-4">{service.title}</CardTitle>
                    <CardDescription className="text-lg mb-6">
                      {service.description}
                    </CardDescription>
                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={`bg-secondary/30 flex items-center justify-center p-8 ${index % 2 === 1 ? 'md:order-first' : ''}`}>
                    <div className="text-center">
                      <div className="text-6xl mb-4">{getIcon(service.icon)}</div>
                      <p className="text-muted-foreground">Professional care you can trust</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Pricing Section */}
      <section className="section-padding bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Transparent pricing with no hidden fees
            </p>
          </div>

          <div className="max-w-6xl mx-auto space-y-8">
            {/* Deposit Notice */}
            <div className="bg-primary text-white p-6 rounded-xl text-center space-y-3">
              <p className="font-bold text-lg">
                A 50% Deposit is due within 48 hours of confirmed booking to secure your booking. At this time we are only able to accept payment via e-transfer to jdecker@sadiesmission.com
              </p>
              <p className="font-bold text-lg">
                The final payment of the remaining amount owing (50%) is due 6 days prior to your pet's check-in date for regular season bookings and 13 days prior to your pet's check-in date for peak season bookings.
              </p>
              <p className="font-bold text-lg">
                Full payment is due upon booking for Day Boarding (including booking multiple days in advance or pre-paying for a pass).
              </p>
            </div>

            {/* Premium Booking Charge Notice */}
            <Card className="border-2 border-primary bg-primary/5">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <DollarSign className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-base text-foreground leading-relaxed">
                      <strong className="text-lg">Please Note:</strong> There will be a <strong>premium booking charge of $5 per 24 hours</strong> during peak booking seasons and all statutory holidays.
                    </p>
                    <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Statutory Holidays:</strong> All statutory holiday days recognized by the Province of BC (excluding Easter Monday)</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary mt-1">•</span>
                        <span><strong>Peak Booking Seasons:</strong> Spring Break, Summer Break, and Winter Break (following School District 42's closure dates)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dog Boarding Pricing */}
            <Card className="overflow-hidden border-2">
              <CardHeader className="bg-primary/5">
                <CardTitle className="text-2xl">{detailedPricing.dogBoarding.title}</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {detailedPricing.dogBoarding.options.map((option, idx) => (
                    <div key={idx} className="border rounded-lg p-6 hover:border-primary/50 transition-all">
                      <div className="flex items-baseline justify-between mb-3">
                        <h3 className="text-xl font-bold text-foreground">{option.name}</h3>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-primary">{option.price}</div>
                          <div className="text-sm text-muted-foreground">{option.unit}</div>
                        </div>
                      </div>
                      {option.savings && (
                        <Badge className="mb-3 bg-green-100 text-green-800 hover:bg-green-100">
                          {option.savings}
                        </Badge>
                      )}
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p className="text-sm text-blue-900">{detailedPricing.dogBoarding.note}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tailored Care Pricing */}
            <Card className="overflow-hidden border-2">
              <CardHeader className="bg-accent/5">
                <CardTitle className="text-2xl flex items-center">
                  {detailedPricing.tailoredCare.title}
                  <Badge variant="secondary" className="ml-3">Premium Service</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {detailedPricing.tailoredCare.options.map((option, idx) => (
                    <div key={idx} className="border rounded-lg p-6 hover:border-primary/50 transition-all bg-accent/5">
                      <div className="flex items-baseline justify-between mb-3">
                        <h3 className="text-xl font-bold text-foreground">{option.name}</h3>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-primary">{option.price}</div>
                          <div className="text-sm text-muted-foreground">{option.unit}</div>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                  <p className="text-sm text-amber-900">{detailedPricing.tailoredCare.note}</p>
                </div>
              </CardContent>
            </Card>

            {/* Dog Boarding Cancellation Policy Banner */}
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg">
              <p className="text-lg font-bold text-orange-900 mb-4">Cancellation Policy:</p>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-orange-900">
                    <strong>Regular Season Bookings:</strong> A minimum of 7 days notice prior to your pets check-in date is required to receive a full refund of your 50% deposit. Once the final payment is received, there are no refunds.
                  </p>
                </div>
                <div>
                  <p className="text-sm text-orange-900">
                    <strong>Peak Season Bookings:</strong> A minimum of 14 days notice prior to your pets check-in date is required to receive a full refund of your 50% deposit. Once the final payment is received, there are no refunds.
                  </p>
                </div>
              </div>
            </div>

            {/* Day Boarding Note */}
            <Card className="border-2">
              <CardHeader className="bg-secondary/5">
                <CardTitle className="text-2xl">{detailedPricing.dayBoarding.title}</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {detailedPricing.dayBoarding.options.map((option, idx) => (
                    <div key={idx} className="border rounded-lg p-6 hover:border-primary/50 transition-all">
                      <div className="flex items-baseline justify-between mb-3">
                        <h3 className="text-xl font-bold text-foreground">{option.name}</h3>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-primary">{option.price}</div>
                          <div className="text-sm text-muted-foreground">{option.unit}</div>
                        </div>
                      </div>
                      {option.savings && (
                        <Badge className="mb-3 bg-green-100 text-green-800 hover:bg-green-100">
                          {option.savings}
                        </Badge>
                      )}
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p className="text-sm text-blue-900">{detailedPricing.dayBoarding.note}</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <p className="text-sm text-green-900 font-medium">
                      💰 {detailedPricing.dayBoarding.bulkDiscount}
                    </p>
                  </div>
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                    <p className="text-sm text-orange-900">
                      <strong>Cancellation Policy:</strong> A minimum of <strong>24 hours notice</strong> is required to receive a full refund and for a no-show or cancellation under 24 hours, a credit will be applied to your account for future use.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Additional Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Extra care options to make your dog's stay even more special
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {additionalServices.map((service, index) => (
              <Card 
                key={index} 
                className={`hover-lift text-center ${service === "Spa Baths" ? "cursor-pointer border-2 border-primary/50 hover:border-primary" : ""}`}
                onClick={() => service === "Spa Baths" && setSpaBathsOpen(true)}
              >
                <CardContent className="pt-6">
                  {service === "Spa Baths" ? (
                    <Sparkles className="h-8 w-8 text-primary mx-auto mb-3" />
                  ) : (
                    <CheckCircle className="h-8 w-8 text-primary mx-auto mb-3" />
                  )}
                  <p className="font-medium text-foreground">{service}</p>
                  {service === "Spa Baths" && (
                    <p className="text-sm text-primary mt-2 font-medium">Click for details & pricing</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-muted-foreground mb-4">Contact us for pricing on additional services</p>
            <Link to="/contact">
              <Button variant="outline">Get in Touch</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Choose Sadie's Mission?
            </h2>
            <p className="text-lg text-muted-foreground">
              We go above and beyond to ensure your dog has the best experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className="hover-lift text-center border-2">
                <CardHeader>
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                    <item.icon className="h-8 w-8" />
                  </div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                  <CardDescription className="text-base">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Book your dog's stay today and experience the Sadie's Mission difference
          </p>
          <Link to="/booking">
            <Button size="lg" variant="secondary">
              Book Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
